'use client';

/**
 * "Free In-Home Consultation" calls-to-action (owner request 2026-10-25: the
 * offer was hard to find on the homepage). Online shopping stays the primary
 * path, so these sit beside — never in place of — the Shop buttons.
 *
 * Colour: one dedicated accent, green-700 #15803D with white text (contrast
 * 5.0:1, WCAG AA), used ONLY for consultation CTAs so they stand apart from
 * the blue Shop buttons and the red/gold brand colours.
 *
 * Claims are the owner-confirmed facts only: free, in-home, Redmond & Eastside,
 * $2M insured, background-checked installers, same-week availability.
 */
import Link from 'next/link';
import { CalendarCheck, Phone, ShieldCheck, UserCheck, Clock } from 'lucide-react';
import { useContactInfo } from '@/context/SiteConfigContext';
import { trackConsultCta } from '@/lib/analytics/leadTracking';

export const CONSULT_HREF = '/consultation#consultation-form';
export const CONSULT_BTN =
  'bg-[#15803D] hover:bg-[#166534] text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#15803D]';

/** Header button (md and up). */
export function HeaderConsultButton({ className = '' }: { className?: string }) {
  return (
    <Link
      prefetch={false}
      href={CONSULT_HREF}
      data-track="consult-header"
      onClick={() => trackConsultCta('header')}
      className={`${CONSULT_BTN} inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-bold whitespace-nowrap shadow-sm ${className}`}
    >
      <CalendarCheck size={16} aria-hidden="true" /> Free Consultation
    </Link>
  );
}

/** Slim strip directly under the header, above the hero banners. */
export function HomeConsultStrip() {
  const { phone, phoneTel } = useContactInfo();
  return (
    <div className="bg-[#15803D] text-white">
      <div className="container mx-auto px-3 md:px-4 py-2 md:py-2.5 flex flex-col md:flex-row md:items-center md:justify-center gap-1.5 md:gap-5 text-center md:text-left">
        {/* Phones get a one-line version so the Shop button stays in the first screen. */}
        <p className="md:hidden text-[13px] font-semibold leading-tight">
          Whole-house project? Free in-home consultation <span className="font-normal opacity-90">· Redmond &amp; Eastside</span>
        </p>
        <p className="hidden md:block text-base font-semibold leading-snug">
          Whole-house project? <span className="whitespace-nowrap">Book a free in-home consultation</span>
          <span className="font-normal opacity-90"> — Redmond &amp; Eastside</span>
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Link
            prefetch={false}
            href={CONSULT_HREF}
            data-track="consult-hero-strip"
            onClick={() => trackConsultCta('hero_strip')}
            className="inline-flex items-center gap-1.5 h-11 md:h-9 px-4 rounded-full bg-white text-[#14532D] text-sm font-bold whitespace-nowrap hover:bg-green-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#15803D]"
          >
            <CalendarCheck size={16} aria-hidden="true" /> Free Consultation
          </Link>
          {phoneTel && phone && (
            <a
              href={phoneTel}
              data-track="consult-hero-strip-call"
              className="inline-flex items-center gap-1.5 h-11 md:h-9 px-3 rounded-full border border-white/70 text-white text-sm font-semibold whitespace-nowrap hover:bg-white/10"
            >
              <Phone size={15} aria-hidden="true" /> {phone}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** Short homepage section further down: the offer plus the form link. */
export function HomeConsultSection() {
  const { phone, phoneTel } = useContactInfo();
  const points = [
    { icon: CalendarCheck, text: 'Free, no-obligation visit: we measure every window and bring samples' },
    { icon: Clock, text: 'Same-week availability' },
    { icon: UserCheck, text: 'Background-checked installers' },
    { icon: ShieldCheck, text: '$2M insured' },
  ];
  return (
    <section id="free-consultation" className="py-10 md:py-14 bg-green-50 border-y border-green-100">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#15803D] mb-2">Redmond &amp; Eastside</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Planning a whole-house project?</h2>
            <p className="text-gray-700 mb-4">Shop online any time, or have us come to you for a free in-home design consultation.</p>
            <ul className="space-y-2">
              {points.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-2 text-gray-800 text-sm md:text-base">
                  <Icon size={18} className="text-[#15803D] mt-0.5 shrink-0" aria-hidden="true" /> {text}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              prefetch={false}
              href={CONSULT_HREF}
              data-track="consult-home-section"
              onClick={() => trackConsultCta('home_section')}
              className={`${CONSULT_BTN} inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg text-base font-bold`}
            >
              <CalendarCheck size={18} aria-hidden="true" /> Book a Free Consultation
            </Link>
            {phoneTel && phone && (
              <a href={phoneTel} data-track="consult-home-section-call" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg border border-gray-900 text-gray-900 font-semibold hover:bg-white">
                <Phone size={18} aria-hidden="true" /> Call {phone}
              </a>
            )}
            <p className="text-xs text-gray-600 text-center">Takes under a minute. We&rsquo;ll call to set a time.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
